import { format } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import { Pagination, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import styled from "styled-components";
import Swal from "sweetalert2";
import { ADMIN_BADGE, CODE_QNA_K } from "../../../common/constants";
import { formatDate } from "../../../common/format";
import AdminHeadTitle from "../../../components/AdminHeadTitle";
import { RootState } from "../../../redux/store";
import { reqSendAlarm } from "../../../requests/alarm";
import { reqReadAdminBadge } from "../../../requests/badge";
import {
  reqQuestionAndResponse,
  reqUpdateQuestionAndResponse,
  reqUploadAnswerImage,
} from "../../../requests/user";

interface Response {
  id: number;
  title: string;
  userType: boolean;
  userName: string;
  categoryId: number;
  questionContent: string;
  createdAt: string;
  responseAt?: string;
  responseContent: string;
  status: number;
  email: string;
  userId: string;
  manager: string;
  imageUrl?: string;
  answerImageUrl?: string;
}

const Aqna: React.FC = () => {
  const [responses, setResponses] = useState<Response[]>([]);
  const [responseContent, setResponseContent] = useState("");
  const admin = useSelector((state: RootState) => state.admin);
  const [selectedResponse, setSelectedResponse] = useState<Response | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [answerImageModalOpen, setAnswerImageModalOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [categoryFilter, setCategoryFilter] = useState<string>("0");
  const [spaceCodeFilter, setSpaceCodeFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [totalPages, setTotalPages] = useState(0);

  const maxPagesToShow = 5;
  const [pageGroupStart, setPageGroupStart] = useState(1);

  const calculatePageGroupStart = (page: number) => {
    const groupNumber = Math.ceil(page / maxPagesToShow);
    return (groupNumber - 1) * maxPagesToShow + 1;
  };

  useEffect(() => {
    setPageGroupStart(calculatePageGroupStart(currentPage));
  }, [currentPage, maxPagesToShow]);

  useEffect(() => {
    fetchResponses();

    const badgePayload = {
      badgeName: ADMIN_BADGE.QNA,
    };
    reqReadAdminBadge(badgePayload);

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };

    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  const fetchResponses = async () => {
    try {
      setIsFetching(true);
      const response = await reqQuestionAndResponse();
      setResponses(response.data);
      setTotalPages(Math.ceil(response.data.length / itemsPerPage));
    } catch (error) {
      console.error("Failed to fetch questions and responses:", error);
      Swal.fire({
        icon: "error",
        title: "데이터 로딩 실패",
        text: "질문 응답 목록을 불러오는데 실패했습니다.",
      });
    } finally {
      setIsFetching(false);
    }
  };

  const handleStatusClick = (response: Response) => {
    setSelectedResponse(response);
    setResponseContent(response.responseContent || "");
    setIsModalOpen(true);
  };

  const handleDeleteClick = async (responseId: number) => {
    Swal.fire({
      title: "정말 삭제하시겠습니까?",
      text: "삭제된 후에는 복구할 수 없습니다.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "삭제",
      cancelButtonText: "취소",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsLoading(true);
          await reqUpdateQuestionAndResponse(responseId.toString(), {
            userType: true,
          });
          setResponses(responses.filter((item) => item.id !== responseId));
          Swal.fire("삭제 완료", "해당 응답이 삭제되었습니다.", "success");
        } catch (error) {
          console.error("Failed to delete response:", error);
          Swal.fire("삭제 실패", "해당 응답을 삭제하지 못했습니다.", "error");
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleResetResponse = () => {
    if (selectedResponse) {
      setResponseContent(selectedResponse.responseContent || "");
    }
  };

  const closeModal = () => {
    setSelectedResponse(null);
    setIsModalOpen(false);
  };

  const getQuestionType = (categoryId: number) => {
    return CODE_QNA_K[categoryId] || "Unknown";
  };

  const handleResponseSubmit = async () => {
    if (!selectedResponse) {
      return;
    }

    Swal.fire({
      title: "답변을 전송하시겠습니까?",
      text: "사용자에게 알림을 전송합니다.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "승인",
      cancelButtonText: "취소",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        // 로딩 팝업 표시
        const loadingPopup = Swal.fire({
          title: "처리 중...",
          html: "답변을 저장하고 알림을 전송하고 있습니다.",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        try {
          setIsLoading(true);
          const formattedResponseAt = format(
            new Date(),
            "yyyy-MM-dd'T'HH:mm:ss'Z'"
          );
          const updatedResponse = {
            ...selectedResponse,
            status: 1,
            responseAt: formattedResponseAt,
            manager: admin.name,
            responseContent: responseContent,
          };

          // 응답 업데이트 요청
          await reqUpdateQuestionAndResponse(
            selectedResponse.id.toString(),
            updatedResponse
          );

          // 상태 업데이트
          setResponses(
            responses.map((item) =>
              item.id === selectedResponse.id ? updatedResponse : item
            )
          );

          // 알림 전송
          const alarmPayload = {
            userId: selectedResponse.userId,
            read: 0,
            content: `${admin.name} has answered your question.`,
            sender: admin.name,
          };
          await reqSendAlarm(alarmPayload);

          // 로딩 팝업 닫기
          Swal.close();

          // 성공 메시지
          Swal.fire({
            icon: "success",
            title: "완료!",
            text: "답변이 저장되고 알림이 전송되었습니다.",
          });

          closeModal();
        } catch (error) {
          console.error("Failed to submit response:", error);

          // 로딩 팝업 닫기
          Swal.close();

          // 오류 메시지
          Swal.fire({
            icon: "error",
            title: "오류 발생",
            text: "답변 처리 중 문제가 발생했습니다.",
          });
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleResponseContentChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setResponseContent(event.target.value);
  };

  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !selectedResponse) return;

    const file = files[0];

    try {
      setIsImageUploading(true);

      const response = await reqUploadAnswerImage(
        selectedResponse.id.toString(),
        file
      );

      if (response.data.success) {
        // 응답에서 이미지 URL 가져오기
        const answerImageUrl = response.data.answerImageUrl;

        // 선택된 응답 객체 업데이트
        const updatedResponse = {
          ...selectedResponse,
          answerImageUrl,
        };

        // 상태 업데이트
        setSelectedResponse(updatedResponse);

        // 전체 응답 목록에서도 해당 항목 업데이트
        setResponses(
          responses.map((item) =>
            item.id === selectedResponse.id ? updatedResponse : item
          )
        );

        Swal.fire({
          icon: "success",
          title: "이미지 업로드 성공",
          text: "답변 이미지가 업로드되었습니다.",
        });
      }
    } catch (error) {
      console.error("Failed to upload answer image:", error);
      Swal.fire({
        icon: "error",
        title: "이미지 업로드 실패",
        text: "답변 이미지 업로드 중 오류가 발생했습니다.",
      });
    } finally {
      setIsImageUploading(false);
      // 파일 입력 초기화
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleAnswerImageClick = () => {
    if (selectedResponse && selectedResponse.answerImageUrl) {
      setSelectedImageUrl(selectedResponse.answerImageUrl);
      setAnswerImageModalOpen(true);
    }
  };

  const filteredResponses = responses
    .filter((response) => {
      const categoryMatch =
        categoryFilter === "0" ||
        response.categoryId === parseInt(categoryFilter, 10);
      const spaceCodeMatch = response.userId
        .toLowerCase()
        .includes(spaceCodeFilter.toLowerCase());

      return categoryMatch && spaceCodeMatch;
    })
    .sort((a, b) => {
      if (a.status === b.status) {
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      }
      return a.status - b.status;
    });

  useEffect(() => {
    setTotalPages(Math.ceil(filteredResponses.length / itemsPerPage));
    // 필터 변경 시 첫 페이지로 돌아가기
    setCurrentPage(1);
  }, [filteredResponses.length]);

  const currentResponses = filteredResponses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleNextPageGroup = () => {
    if (pageGroupStart + maxPagesToShow <= totalPages) {
      setPageGroupStart(pageGroupStart + maxPagesToShow);
      setCurrentPage(pageGroupStart + maxPagesToShow);
    }
  };

  const handlePreviousPageGroup = () => {
    if (pageGroupStart - maxPagesToShow >= 1) {
      setPageGroupStart(pageGroupStart - maxPagesToShow);
      setCurrentPage(pageGroupStart - maxPagesToShow);
    }
  };

  const renderPaginationControls = () => {
    const startPage = pageGroupStart;
    const endPage = Math.min(startPage + maxPagesToShow - 1, totalPages);

    return (
      <PaginationContainer>
        <StyledPagination>
          <Pagination.First
            onClick={() => {
              setCurrentPage(1);
              setPageGroupStart(1);
            }}
            disabled={currentPage === 1}
          />
          <Pagination.Prev
            onClick={() => {
              if (currentPage > 1) {
                const newPage = currentPage - 1;
                setCurrentPage(newPage);
              }
            }}
            disabled={currentPage === 1}
          />
          {Array.from(
            { length: endPage - startPage + 1 },
            (_, i) => startPage + i
          ).map((pageNumber) => (
            <Pagination.Item
              key={pageNumber}
              active={pageNumber === currentPage}
              onClick={() => handlePageChange(pageNumber)}
            >
              {pageNumber}
            </Pagination.Item>
          ))}
          <Pagination.Next
            onClick={() => {
              if (currentPage < totalPages) {
                const newPage = currentPage + 1;
                setCurrentPage(newPage);
              }
            }}
            disabled={currentPage === totalPages}
          />
          <Pagination.Last
            onClick={() => {
              setCurrentPage(totalPages);
              const lastGroupStart =
                Math.floor((totalPages - 1) / maxPagesToShow) * maxPagesToShow +
                1;
              setPageGroupStart(lastGroupStart);
            }}
            disabled={currentPage === totalPages}
          />
        </StyledPagination>
      </PaginationContainer>
    );
  };

  return (
    <MainContainer id="main" className="main">
      <AdminHeadTitle
        subtitle1="서비스 관리"
        subtitle2="질문 응답"
        title="질문 응답"
      />

      <ContentWrapper>
        <FilterContainer>
          <FilterRow>
            <FilterCol>
              <label htmlFor="categoryFilter" className="form-label">
                질문 유형
              </label>
              <StyledSelect
                id="categoryFilter"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="0">모두</option>
                {Object.entries(CODE_QNA_K).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </StyledSelect>
            </FilterCol>
            <FilterCol>
              <label htmlFor="spaceCodeFilter" className="form-label">
                스페이스 코드
              </label>
              <StyledInput
                type="text"
                id="spaceCodeFilter"
                value={spaceCodeFilter}
                onChange={(e) => setSpaceCodeFilter(e.target.value)}
              />
            </FilterCol>
            <FilterCol className="align-end">
              <RefreshButton onClick={fetchResponses} disabled={isFetching}>
                {isFetching ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    로딩중...
                  </>
                ) : (
                  "새로고침"
                )}
              </RefreshButton>
            </FilterCol>
          </FilterRow>
        </FilterContainer>

        <TableContainer>
          <StyledTable>
            <thead>
              <tr>
                <th>질문유형</th>
                <th>스페이스 코드</th>
                <th className="d-none d-md-table-cell">이름</th>
                <th className="d-none d-md-table-cell">등록일</th>
                <th className="d-none d-md-table-cell">답변일</th>
                <th className="d-none d-lg-table-cell">담당자</th>
                <th>제목</th>
                <th>상태</th>
                <th>삭제</th>
              </tr>
            </thead>
            <tbody>
              {currentResponses
                .filter((item) => !item.userType)
                .map((item) => (
                  <StyledTableRow
                    key={item.id}
                    onClick={() => handleStatusClick(item)}
                  >
                    <td>{getQuestionType(item.categoryId)}</td>
                    <td>{item.userId}</td>
                    <td className="d-none d-md-table-cell">{item?.userName}</td>
                    <td className="d-none d-md-table-cell">
                      {formatDate(item.createdAt)}
                    </td>
                    <td className="d-none d-md-table-cell">
                      {item.responseAt
                        ? formatDate(item.responseAt)
                        : "답변 대기중"}
                    </td>
                    <td className="d-none d-lg-table-cell">
                      {item.manager || "담당자 미지정"}
                    </td>
                    <td>{item.title}</td>
                    <td>
                      <StatusBadge status={item.status}>
                        {item.status ? "완료" : "미완료"}
                      </StatusBadge>
                    </td>
                    <td>
                      <DeleteButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(item.id);
                        }}
                        disabled={isLoading}
                      >
                        삭제
                      </DeleteButton>
                    </td>
                  </StyledTableRow>
                ))}
            </tbody>
          </StyledTable>

          {currentResponses.length === 0 && !isFetching && (
            <EmptyStateContainer>
              <EmptyStateMessage>질문 데이터가 없습니다.</EmptyStateMessage>
            </EmptyStateContainer>
          )}

          {isFetching && (
            <LoadingContainer>
              <StyledSpinner animation="border" role="status">
                <span className="visually-hidden">로딩중...</span>
              </StyledSpinner>
              <LoadingMessage>데이터를 불러오는 중입니다...</LoadingMessage>
            </LoadingContainer>
          )}
        </TableContainer>
      </ContentWrapper>

      {renderPaginationControls()}

      {/* Response Modal */}
      {selectedResponse && (
        <StyledModal
          className={isModalOpen ? "show" : ""}
          tabIndex={-1}
          role="dialog"
          style={{ display: isModalOpen ? "block" : "none" }}
        >
          <ModalDialog>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>{selectedResponse.title}</ModalTitle>
                <CloseButton
                  type="button"
                  onClick={closeModal}
                  aria-label="Close"
                >
                  &times;
                </CloseButton>
              </ModalHeader>
              <ModalBody>
                <InfoCard>
                  <CardHeader>
                    <InfoTable>
                      <tbody>
                        <tr>
                          <th>질문유형</th>
                          <td>
                            {getQuestionType(selectedResponse.categoryId)}
                          </td>
                        </tr>
                        <tr>
                          <th>고객유형</th>
                          <td>
                            {selectedResponse.userType ? "비회원" : "회원"}
                          </td>
                        </tr>
                        <tr>
                          <th>스페이스코드</th>
                          <td>{selectedResponse.userId}</td>
                        </tr>
                        <tr>
                          <th>이름</th>
                          <td>{selectedResponse?.userName}</td>
                        </tr>
                        <tr>
                          <th>이메일</th>
                          <td>{selectedResponse.email}</td>
                        </tr>
                        {selectedResponse.imageUrl && (
                          <tr>
                            <th>이미지</th>
                            <td>
                              <QuestionImage
                                src={selectedResponse.imageUrl}
                                alt="Question image"
                                onClick={() => {
                                  if (selectedResponse.imageUrl) {
                                    setSelectedImageUrl(
                                      selectedResponse.imageUrl
                                    );
                                    setImageModalOpen(true);
                                  }
                                }}
                              />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </InfoTable>
                  </CardHeader>
                  <CardBody>
                    <ContentTitle>내용</ContentTitle>
                    <QuestionContent>
                      {selectedResponse.questionContent}
                    </QuestionContent>
                  </CardBody>
                  <CardFooter>
                    <TextareaWrapper>
                      <ResponseTextarea
                        placeholder="답변을 입력하세요"
                        value={responseContent}
                        onChange={handleResponseContentChange}
                      />
                      <TextareaLabel>답변</TextareaLabel>
                    </TextareaWrapper>

                    <ImageUploadSection>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                      <UploadButton
                        type="button"
                        onClick={handleFileSelect}
                        disabled={isImageUploading}
                      >
                        {isImageUploading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              className="me-2"
                            />
                            업로드 중...
                          </>
                        ) : (
                          "이미지 업로드"
                        )}
                      </UploadButton>

                      {selectedResponse.answerImageUrl && (
                        <AnswerImagePreview>
                          <PreviewImage
                            src={selectedResponse.answerImageUrl}
                            alt="Answer preview"
                            onClick={handleAnswerImageClick}
                          />
                          <PreviewCaption>
                            미리보기 (클릭하여 확대)
                          </PreviewCaption>
                        </AnswerImagePreview>
                      )}
                    </ImageUploadSection>
                  </CardFooter>
                </InfoCard>
              </ModalBody>
              <ModalFooter>
                <SubmitButton
                  type="button"
                  onClick={handleResponseSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        className="me-2"
                      />
                      전송중...
                    </>
                  ) : (
                    "전송"
                  )}
                </SubmitButton>
                <CancelButton
                  type="button"
                  onClick={closeModal}
                  disabled={isLoading}
                >
                  닫기
                </CancelButton>
              </ModalFooter>
            </ModalContent>
          </ModalDialog>
        </StyledModal>
      )}

      {/* Image Preview Modal */}
      {imageModalOpen && (
        <StyledModal
          className="show"
          tabIndex={-1}
          role="dialog"
          style={{ display: "block" }}
        >
          <ModalDialog>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>이미지 보기</ModalTitle>
                <CloseButton
                  type="button"
                  onClick={() => setImageModalOpen(false)}
                  aria-label="Close"
                >
                  &times;
                </CloseButton>
              </ModalHeader>
              <ModalBody>
                <ImageContainer>
                  {selectedImageUrl && (
                    <FullImage src={selectedImageUrl} alt="Question image" />
                  )}
                </ImageContainer>
              </ModalBody>
              <ModalFooter>
                <CancelButton
                  type="button"
                  onClick={() => setImageModalOpen(false)}
                >
                  닫기
                </CancelButton>
              </ModalFooter>
            </ModalContent>
          </ModalDialog>
        </StyledModal>
      )}

      {/* Answer Image Preview Modal */}
      {answerImageModalOpen && (
        <StyledModal
          className="show"
          tabIndex={-1}
          role="dialog"
          style={{ display: "block" }}
        >
          <ModalDialog>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>답변 이미지 보기</ModalTitle>
                <CloseButton
                  type="button"
                  onClick={() => setAnswerImageModalOpen(false)}
                  aria-label="Close"
                >
                  &times;
                </CloseButton>
              </ModalHeader>
              <ModalBody>
                <ImageContainer>
                  {selectedImageUrl && (
                    <FullImage src={selectedImageUrl} alt="Answer image" />
                  )}
                </ImageContainer>
              </ModalBody>
              <ModalFooter>
                <CancelButton
                  type="button"
                  onClick={() => setAnswerImageModalOpen(false)}
                >
                  닫기
                </CancelButton>
              </ModalFooter>
            </ModalContent>
          </ModalDialog>
        </StyledModal>
      )}

      {/* Modal backdrop */}
      {(isModalOpen || imageModalOpen || answerImageModalOpen) && <Backdrop />}
    </MainContainer>
  );
};

export default Aqna;

// Styled Components
const MainContainer = styled.main`
  padding: 20px;
  background-color: #f8f9fa;
`;

const ContentWrapper = styled.div`
  margin-bottom: 20px;
`;

const FilterContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin: 0 -8px;
`;

const FilterCol = styled.div`
  flex: 1;
  min-width: 200px;
  padding: 0 8px;
  margin-bottom: 16px;

  &.align-end {
    display: flex;
    align-items: flex-end;
  }

  @media (max-width: 768px) {
    flex-basis: 100%;
  }
`;

const StyledSelect = styled.select`
  display: block;
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: #fff;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    border-color: #86b7fe;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
    outline: 0;
  }
`;

const StyledInput = styled.input`
  display: block;
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: #fff;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    border-color: #86b7fe;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
    outline: 0;
  }
`;

const RefreshButton = styled.button`
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: white;
  background-color: #0d6efd;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0b5ed7;
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const TableContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  th {
    background-color: #f8f9fa;
    padding: 12px 16px;
    font-weight: 600;
    text-align: center;
    border-bottom: 1px solid #dee2e6;
    color: #495057;
    font-size: 14px;
  }

  td {
    padding: 12px 16px;
    vertical-align: middle;
    border-bottom: 1px solid #e9ecef;
    font-size: 14px;
    text-align: center;
  }

  @media (max-width: 768px) {
    th,
    td {
      padding: 8px 12px;
      font-size: 13px;
    }
  }
`;

const StyledTableRow = styled.tr`
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f1f5fe;
  }
`;

const StatusBadge = styled.span<{ status: number }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  background-color: ${(props) => (props.status ? "#38b27c" : "#ff6b6b")};
  color: white;
`;

const DeleteButton = styled.button`
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background-color: #ff6b6b;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #e74c3c;
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
`;

const EmptyStateMessage = styled.p`
  font-size: 16px;
  color: #6c757d;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
`;

const StyledSpinner = styled(Spinner)`
  color: #0d6efd;
  margin-bottom: 16px;
`;

const LoadingMessage = styled.p`
  font-size: 14px;
  color: #6c757d;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const StyledPagination = styled(Pagination)`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;

  .page-item .page-link {
    border-radius: 4px;
    color: #495057;
    border: 1px solid #dee2e6;
    padding: 6px 12px;
    transition: all 0.2s;
  }

  .page-item.active .page-link {
    background-color: #0d6efd;
    border-color: #0d6efd;
  }

  .page-item:not(.active) .page-link:hover {
    background-color: #e9ecef;
  }

  @media (max-width: 768px) {
    .page-item .page-link {
      padding: 4px 8px;
      font-size: 13px;
    }
  }
`;

// Modal Styled Components
const StyledModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1050;
  overflow-x: hidden;
  overflow-y: auto;
  outline: 0;
`;

const ModalDialog = styled.div`
  position: relative;
  width: auto;
  margin: 1.75rem auto;
  max-width: 800px;

  @media (max-width: 992px) {
    max-width: 90%;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  pointer-events: auto;
  background-color: #fff;
  background-clip: padding-box;
  border: none;
  border-radius: 8px;
  outline: 0;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #e9ecef;
`;

const ModalTitle = styled.h1`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #343a40;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  font-weight: 600;
  color: #6c757d;
  cursor: pointer;
`;

const ModalBody = styled.div`
  position: relative;
  flex: 1 1 auto;
  padding: 24px;
`;

const ModalFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  gap: 12px;
`;

const InfoCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
`;

const CardHeader = styled.div`
  padding: 16px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
`;

const CardBody = styled.div`
  padding: 20px;
  background-color: white;
`;

const CardFooter = styled.div`
  padding: 16px;
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
`;

const InfoTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 8px 12px;
    text-align: left;
    font-size: 14px;
    border-bottom: 1px solid #e9ecef;
  }

  th {
    width: 120px;
    font-weight: 600;
    color: #495057;
  }

  tr:last-child th,
  tr:last-child td {
    border-bottom: none;
  }
`;

const ContentTitle = styled.h5`
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #343a40;
`;

const QuestionContent = styled.p`
  white-space: pre-line;
  font-size: 14px;
  line-height: 1.6;
  color: #495057;
  background-color: #f8f9fa;
  padding: 16px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
`;

const TextareaWrapper = styled.div`
  position: relative;
`;

const ResponseTextarea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 16px;
  font-size: 14px;
  line-height: 1.6;
  color: #495057;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 6px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    border-color: #86b7fe;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
    outline: 0;
  }

  &:focus + label {
    color: #0d6efd;
  }
`;

const TextareaLabel = styled.label`
  position: absolute;
  top: 0;
  left: 16px;
  transform: translateY(-50%);
  padding: 0 4px;
  background-color: #f8f9fa;
  font-size: 14px;
  color: #6c757d;
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: white;
  background-color: #0d6efd;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0b5ed7;
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: white;
  background-color: #6c757d;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #5a6268;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const ImageContainer = styled.div`
  text-align: center;
`;

const FullImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 6px;
`;

const QuestionImage = styled.img`
  max-width: 100px;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
`;

const ImageUploadSection = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const UploadButton = styled.button`
  padding: 10px 16px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;
  align-self: flex-start;

  &:hover {
    background-color: #5a6268;
  }

  &:disabled {
    background-color: #adb5bd;
    cursor: not-allowed;
  }
`;

const AnswerImagePreview = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const PreviewImage = styled.img`
  max-width: 200px;
  max-height: 150px;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const PreviewCaption = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin: 0;
`;
